import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { SplitType } from '../../invitations/models/invitation.model';
import { Session, SPLIT_TYPE_LABELS } from '../models/session.model';
import { SessionMockService } from '../services/session-mock.service';

@Component({
  selector: 'app-sessions-list',
  templateUrl: './sessions-list.component.html',
  styleUrls: ['./sessions-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionsListComponent implements OnInit {
  rows: Session[] = [];
  search = '';
  statusFilter: 'all' | 'active' | 'expired' = 'all';
  splitFilter: SplitType | 'all' = 'all';
  /** Split-type options for the filter dropdown, from the shared label map. */
  splitTypes: SplitType[] = ['similar', 'diverse_exact'];
  sortKey: 'name' | 'primaryInvitationName' | 'createdAt' | 'expiresAt' = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  page = 0;
  pageSize = 10;
  pageSizes = [5, 10, 15, 20, 25];

  private all: Session[] = [];

  constructor(private svc: SessionMockService, private router: Router) {}

  ngOnInit(): void { this.reload(); }

  reload(): void {
    this.svc.list().subscribe(rows => { this.all = rows; this.applyView(); });
  }

  /** Filter + sort + paginate in the component: the whole list is local and small. */
  private applyView(): void {
    let rows = [...this.all];
    if (this.statusFilter !== 'all') {
      const wantExpired = this.statusFilter === 'expired';
      rows = rows.filter(s => this.svc.isExpired(s) === wantExpired);
    }
    if (this.splitFilter !== 'all') {
      rows = rows.filter(s => s.splitType === this.splitFilter);
    }
    const s = this.search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(s) ||
        r.primaryInvitationName.toLowerCase().includes(s) ||
        r.createdBy.toLowerCase().includes(s));
    }
    const dir = this.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      // Expiry is optional. Never-expiring rows sink to the BOTTOM in both directions rather than
      // riding the sort: interpolating them would compare the literal string "undefined", which
      // scatters them by direction and reads as a bug. Ties fall through to the normal compare.
      if (this.sortKey === 'expiresAt' && !a.expiresAt !== !b.expiresAt) { return a.expiresAt ? -1 : 1; }
      return `${a[this.sortKey]}`.localeCompare(`${b[this.sortKey]}`) * dir;
    });
    this.count = rows.length;
    // Clamp page here (not just in setPage()): the list can shrink under the current page
    // (delete, filter, search) via reload()/remove(), which call applyView() directly and
    // bypass setPage()'s guard. Without this, count > 0 hides the empty state, pageCount() === 1
    // hides the pager, and the user is stranded on a blank page with no way back.
    const lastPage = Math.max(0, Math.ceil(this.count / this.pageSize) - 1);
    this.page = Math.min(this.page, lastPage);
    this.rows = rows.slice(this.page * this.pageSize, this.page * this.pageSize + this.pageSize);
  }

  count = 0;

  onFilter(): void { this.page = 0; this.applyView(); }
  clearSearch(): void { this.search = ''; this.onFilter(); }
  setPage(p: number): void { this.page = Math.max(0, Math.min(p, this.pageCount() - 1)); this.applyView(); }
  pageCount(): number { return Math.max(1, Math.ceil(this.count / this.pageSize)); }
  pages(): number[] { return Array.from({ length: this.pageCount() }, (_, i) => i); }
  pageStart(): number { return this.count === 0 ? 0 : this.page * this.pageSize + 1; }
  pageEnd(): number { return Math.min((this.page + 1) * this.pageSize, this.count); }

  sortBy(key: 'name' | 'primaryInvitationName' | 'createdAt' | 'expiresAt'): void {
    if (this.sortKey === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
    else { this.sortKey = key; this.sortDir = 'asc'; }
    this.page = 0;
    this.applyView();
  }
  sortIcon(key: 'name' | 'primaryInvitationName' | 'createdAt' | 'expiresAt'): string {
    if (this.sortKey !== key) { return 'fa-sort'; }
    return this.sortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  splitLabel(t: string): string { return SPLIT_TYPE_LABELS[t as keyof typeof SPLIT_TYPE_LABELS] ?? t; }
  isExpired(s: Session): boolean { return this.svc.isExpired(s); }
  statusLabel(s: Session): string { return this.svc.isExpired(s) ? 'Expired' : 'Active'; }
  initials(name: string): string { return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(); }

  create(): void { this.router.navigate(['/activity-groups/new']); }
  edit(s: Session): void { this.router.navigate(['/activity-groups', s.id, 'edit']); }

  /** Opening a session goes straight to its board — unlike Reports, there's no separate "view" gate. */
  open(s: Session): void { this.router.navigate(['/activity-groups', s.id]); }

  /** Clone in place — the copy lands in the listing; edit it from there if it needs changes. */
  duplicate(s: Session, ev: Event): void {
    ev.stopPropagation();
    this.svc.duplicate(s.id).subscribe(copy => {
      this.reload();
      Swal.fire({
        title: 'Activity group cloned', text: `Created "${copy.name}".`, icon: 'success',
        timer: 1600, showConfirmButton: false,
      });
    });
  }

  remove(s: Session, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      title: `Delete "${s.name}"?`, text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#f54359',
    }).then(res => { if (res.isConfirmed) { this.svc.remove(s.id).subscribe(() => this.reload()); } });
  }
}
